/**
 * The capture-the-flag HUD (docs/spellweave-modes.md §2): the score bar (each
 * team's captures, its flag's state, the clock), the scoreboard (every
 * wizard by team with kills, deaths and ping) and the minimap (walls, water,
 * flags and wizards, your own marked).
 */

import type { Arena } from '@shared/index.js';
import type { ViewModel } from '../state/viewModel.js';
import { TEAMS, UI, rgba } from '../palette.js';
import { TAU, font, roundRectPath, type Ctx } from './gfx.js';
import { ctfHudLayout } from './hudLayout.js';

function clock(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const FLAG_WORD = { home: 'home', carried: 'taken', dropped: 'dropped' } as const;

/** Top centre (under the room panel on a phone): ROSE 1 | 9:41 | 2 LIME, with each flag's state under its team. */
export function drawCtfBar(ctx: Ctx, vm: ViewModel, w: number, s: number): void {
  const c = vm.ctf;
  if (!c) return;
  const { x, y, w: bw, h: bh } = ctfHudLayout(w, s).bar;
  const mid = x + bw / 2;
  ctx.fillStyle = UI.panel;
  roundRectPath(ctx, x, y, bw, bh, 8 * s);
  ctx.fill();
  ctx.textBaseline = 'middle';
  for (const team of [0, 1] as const) {
    const side = TEAMS[team];
    const f = c.f[team];
    const cx = team === 0 ? x + 58 * s : x + bw - 58 * s;
    ctx.textAlign = 'center';
    ctx.font = font(13 * s, 800);
    ctx.fillStyle = side.color;
    ctx.fillText(`${side.name.toUpperCase()}  ${c.s[team]}`, cx, y + 16 * s);
    ctx.font = font(10 * s, 700);
    ctx.fillStyle = f.st === 'home' ? UI.muted : f.st === 'carried' ? UI.hpRed : UI.gold;
    ctx.fillText(f.st === 'dropped' ? `flag dropped ${Math.ceil(f.t ?? 0)}s` : `flag ${FLAG_WORD[f.st]}`, cx, y + 32 * s);
  }
  ctx.textAlign = 'center';
  ctx.font = font(15 * s, 800);
  ctx.fillStyle = c.t <= 60 ? UI.hpRed : UI.text;
  ctx.fillText(clock(c.t), mid, y + 16 * s);
  ctx.font = font(9 * s, 700);
  ctx.fillStyle = UI.muted;
  ctx.fillText(`first to ${c.cap}`, mid, y + 32 * s);
}

/** Top right (in place of the ally cards): every wizard by team, kills / deaths, ping. */
export function drawScoreboard(ctx: Ctx, vm: ViewModel, w: number, s: number): number {
  const rowH = 18 * s;
  const { x, y, w: pw } = ctfHudLayout(w, s).board;
  const rows = [...vm.players].sort((a, b) => (a.tm ?? 0) - (b.tm ?? 0));
  const ph = 22 * s + rows.length * rowH;
  ctx.fillStyle = UI.panel;
  roundRectPath(ctx, x, y, pw, ph, 8 * s);
  ctx.fill();
  ctx.textBaseline = 'middle';
  ctx.font = font(9 * s, 800);
  ctx.fillStyle = UI.muted;
  ctx.textAlign = 'left';
  ctx.fillText('WIZARD', x + 10 * s, y + 11 * s);
  ctx.textAlign = 'right';
  ctx.fillText('K / D', x + pw - 52 * s, y + 11 * s);
  ctx.fillText('PING', x + pw - 10 * s, y + 11 * s);
  rows.forEach((p, i) => {
    const ry = y + 22 * s + i * rowH + rowH / 2;
    const side = p.tm !== undefined ? TEAMS[p.tm] : null;
    ctx.fillStyle = side ? side.color : UI.text;
    ctx.beginPath();
    ctx.arc(x + 14 * s, ry, 4 * s, 0, TAU);
    ctx.fill();
    ctx.textAlign = 'left';
    ctx.font = font(11 * s, p.isLocal ? 800 : 600);
    ctx.fillStyle = p.dead ? UI.muted : UI.text;
    const name = p.fl !== undefined ? `${p.name} ⚑` : p.name;
    ctx.fillText(name.length > 16 ? `${name.slice(0, 15)}…` : name, x + 24 * s, ry, pw - 112 * s);
    ctx.textAlign = 'right';
    ctx.fillStyle = UI.text;
    ctx.fillText(`${p.kd?.[0] ?? 0} / ${p.kd?.[1] ?? 0}`, x + pw - 52 * s, ry);
    ctx.fillStyle = (p.pg ?? 0) > 150 ? UI.hpRed : UI.muted;
    ctx.fillText(p.pg !== undefined ? `${p.pg}` : '-', x + pw - 10 * s, ry);
  });
  return y + ph;
}

/** Under the scoreboard (under the room panel with touch controls): the map in miniature, flags and wizards on it. */
export function drawMinimap(ctx: Ctx, vm: ViewModel, arena: Arena, w: number, boardBottom: number, s: number, touch: boolean): void {
  const m = ctfHudLayout(w, s, touch).map;
  const pw = m.w - 8 * s; // the frame below spans the layout width exactly
  const k = pw / arena.w;
  const ph = arena.h * k;
  const x = m.x + 4 * s, y = m.y ?? boardBottom + 8 * s;
  ctx.fillStyle = 'rgba(10,12,18,0.82)';
  roundRectPath(ctx, x - 4 * s, y - 4 * s, pw + 8 * s, ph + 8 * s, 6 * s);
  ctx.fill();
  ctx.fillStyle = 'rgba(80,90,110,0.9)';
  for (const b of arena.blocks) ctx.fillRect(x + b.x * k, y + b.y * k, Math.max(1, b.w * k), Math.max(1, b.h * k));
  ctx.fillStyle = 'rgba(40,110,190,0.75)';
  for (const b of arena.deep) ctx.fillRect(x + b.x * k, y + b.y * k, b.w * k, b.h * k);
  ctx.fillStyle = 'rgba(40,110,190,0.4)';
  for (const z of arena.water) if (z.k === 'rect') ctx.fillRect(x + z.x * k, y + z.y * k, z.w * k, z.h * k);
  ctx.fillStyle = 'rgba(150,160,175,0.9)';
  for (const p of arena.pillars) {
    ctx.beginPath();
    ctx.arc(x + p.x * k, y + p.y * k, Math.max(1.5, p.r * k), 0, TAU);
    ctx.fill();
  }
  if (vm.ctf) {
    vm.ctf.f.forEach((f, i) => {
      const side = TEAMS[i as 0 | 1];
      const fx = x + f.x * k, fy = y + f.y * k;
      ctx.fillStyle = side.color;
      ctx.beginPath();
      ctx.moveTo(fx, fy - 6 * s);
      ctx.lineTo(fx + 6 * s, fy - 3.5 * s);
      ctx.lineTo(fx, fy - 1 * s);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(fx - 0.75 * s, fy - 6 * s, 1.5 * s, 7 * s);
    });
  }
  for (const p of vm.players) {
    if (p.dead) continue;
    const side = p.tm !== undefined ? TEAMS[p.tm] : null;
    const px = x + p.rx * k, py = y + p.ry * k;
    ctx.fillStyle = side ? side.color : UI.text;
    ctx.beginPath();
    ctx.arc(px, py, (p.isLocal ? 3.5 : 2.8) * s, 0, TAU);
    ctx.fill();
    if (p.isLocal) {
      ctx.strokeStyle = rgba('#FFFFFF', 0.9);
      ctx.lineWidth = 1.2 * s;
      ctx.stroke();
    }
  }
}
